import { ARENA_ART as ART, TILE, isSolid, opponentOf, vec, type PlayerId, type State, type WeaponType } from '@chessshooter/shared';
import { COLORS, WEAPON_ART, WEAPON_FINISH, WEAPON_STYLE } from './uiConstants';

const TAU = Math.PI * 2;

export function disc(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, TAU);
  ctx.fill();
}

/** The battlements also appear on the cover: a small visual link to the chess phase. */
function rookMark(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string = COLORS.stoneDark): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size, size);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(-0.5, -0.5);
  ctx.lineTo(-0.2, -0.5);
  ctx.lineTo(-0.2, -0.25);
  ctx.lineTo(0.2, -0.25);
  ctx.lineTo(0.2, -0.5);
  ctx.lineTo(0.5, -0.5);
  ctx.lineTo(0.5, 0);
  ctx.lineTo(0.3, 0.12);
  ctx.lineTo(0.3, 0.35);
  ctx.lineTo(0.5, 0.5);
  ctx.lineTo(-0.5, 0.5);
  ctx.lineTo(-0.3, 0.35);
  ctx.lineTo(-0.3, 0.12);
  ctx.lineTo(-0.5, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export function drawNpcBoxes(ctx: CanvasRenderingContext2D, state: State): void {
  const art = ART.npcBox;
  for (const box of state.npcBoxes) {
    const { x, y } = box.pos;
    const color = ART.tierColors[box.tier];
    disc(ctx, x + ART.shadowX, y + ART.shadowY, box.radius, COLORS.shadow);
    disc(ctx, x, y, box.radius, COLORS.outline);
    disc(ctx, x, y, box.radius - ART.seam, color);
    disc(ctx, x, y, box.radius - art.rim, art.face);
    rookMark(ctx, x, y + art.emblemY, box.radius * art.emblemScale, color);
    const width = box.tier * art.notchWidth + (box.tier - 1) * art.notchGap;
    for (let i = 0; i < box.tier; i++) {
      const nx = x - width / 2 + i * (art.notchWidth + art.notchGap);
      ctx.strokeStyle = art.notchEmpty;
      ctx.lineWidth = ART.seam;
      ctx.strokeRect(nx, y + art.notchY, art.notchWidth, art.notchHeight);
      if (i < box.hp) {
        ctx.fillStyle = color;
        ctx.fillRect(nx, y + art.notchY, art.notchWidth, art.notchHeight);
      }
    }
  }
}

export function drawNpcs(ctx: CanvasRenderingContext2D, state: State, me: PlayerId): void {
  const art = ART.npc;
  for (const npc of state.npcs) {
    const own = npc.ownerId === me;
    const color = own ? COLORS.me : COLORS.foe;
    const light = own ? COLORS.meLight : COLORS.foeLight;
    const r = npc.radius;
    // NPC aim is determined by the simulation's target, even while strafing or retreating.
    const direction = npc.tier === 1 ? (vec.len(npc.vel) > 0 ? npc.vel : npc.moveDir)
      : vec.sub(state.players[opponentOf(npc.ownerId)].pos, npc.pos);
    ctx.save();
    disc(ctx, npc.pos.x + ART.shadowX, npc.pos.y + ART.shadowY, r, COLORS.shadow);
    ctx.translate(npc.pos.x, npc.pos.y);
    ctx.rotate(Math.atan2(direction.y, direction.x));
    for (const sign of [-1, 1]) {
      disc(ctx, r * art.handX, sign * r * art.handY, r * art.handRadius + ART.seam, COLORS.outline);
      disc(ctx, r * art.handX, sign * r * art.handY, r * art.handRadius, light);
    }
    const weaponY = r * art.handY;
    ctx.save();
    ctx.translate(r * art.handX, weaponY);
    if (npc.tier === 1) {
      ctx.fillStyle = COLORS.outline;
      ctx.fillRect(0, -art.knifeGripWidth / 2, art.knifeGripLength, art.knifeGripWidth);
      ctx.beginPath();
      ctx.moveTo(art.knifeGripLength, -art.knifeBladeWidth / 2);
      ctx.lineTo(art.knifeGripLength + art.knifeBladeLength, 0);
      ctx.lineTo(art.knifeGripLength, art.knifeBladeWidth / 2);
      ctx.closePath();
      ctx.fillStyle = art.bladeColor;
      ctx.strokeStyle = COLORS.outline;
      ctx.lineWidth = ART.seam;
      ctx.fill();
      ctx.stroke();
    } else {
      const gun = npc.tier === 2 ? art.pistol : art.rifle;
      ctx.fillStyle = COLORS.outline;
      ctx.fillRect(-ART.seam, -gun.width / 2 - ART.seam, gun.length + ART.seam * 2, gun.width + ART.seam * 2);
      ctx.fillStyle = art.barrelColor;
      ctx.fillRect(0, -gun.width / 2, gun.length, gun.width);
      if (npc.tier === 3) {
        ctx.fillStyle = art.rifleStockColor;
        ctx.fillRect(0, -art.rifleStockWidth / 2, art.rifleStockLength, art.rifleStockWidth);
      }
    }
    ctx.restore();
    disc(ctx, 0, 0, r, COLORS.outline);
    disc(ctx, 0, 0, r - ART.outline, color);
    disc(ctx, -ART.seam, -ART.seam, r * art.highlight, light);
    ctx.fillStyle = COLORS.visor;
    ctx.beginPath();
    ctx.roundRect(r * art.visorX, -r * art.visorHeight / 2, r * art.visorWidth, r * art.visorHeight, ART.crateCorner);
    ctx.fill();
    ctx.restore();
  }
}

/** Static artwork is cached by the renderer, so the tile detail costs nothing per frame. */
export function drawArena(ctx: CanvasRenderingContext2D, state: State): void {
  const { map } = state;
  const cx = Math.floor(map.width / 2);
  const cy = Math.floor(map.height / 2);
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const paved = Math.abs(x - cx) <= 1 || Math.abs(y - cy) <= 1;
      ctx.fillStyle = paved ? ((x + y) % 2 ? COLORS.paving : COLORS.pavingAlt) : ((x + y) % 2 ? COLORS.floor : COLORS.floorAlt);
      ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
      ctx.strokeStyle = COLORS.grid;
      ctx.lineWidth = ART.seam;
      ctx.strokeRect(x * TILE, y * TILE, TILE, TILE);
      // A repeatable stone grain, independent of the simulation RNG.
      if (!paved && (x * 7 + y * 11) % 5 === 0) {
        ctx.fillStyle = COLORS.grid;
        ctx.fillRect(x * TILE + TILE / 3, y * TILE + TILE / 2, ART.crateBrace, ART.seam);
      }
    }
  }
  // Shadows first: neighbouring wall tiles never paint over one another's top faces.
  ctx.fillStyle = COLORS.shadow;
  for (let y = 0; y < map.height; y++) for (let x = 0; x < map.width; x++) {
    if (isSolid(map, x, y)) ctx.fillRect(x * TILE + ART.shadowX, y * TILE + ART.shadowY, TILE, TILE);
  }
  for (let y = 0; y < map.height; y++) for (let x = 0; x < map.width; x++) {
    if (!isSolid(map, x, y)) continue;
    const px = x * TILE;
    const py = y * TILE;
    ctx.fillStyle = COLORS.wallDark;
    ctx.fillRect(px, py, TILE, TILE);
    ctx.fillStyle = COLORS.wall;
    ctx.fillRect(px + ART.wallInset, py + ART.wallInset, TILE - ART.wallInset * 2, TILE - ART.wallBevel);
    ctx.fillStyle = COLORS.wallEdge;
    ctx.fillRect(px + ART.wallInset, py + ART.wallInset, TILE - ART.wallInset * 2, ART.wallBevel);
  }
  for (const p of state.pillars) {
    disc(ctx, p.pos.x + ART.shadowX, p.pos.y + ART.shadowY, p.radius, COLORS.shadow);
    disc(ctx, p.pos.x, p.pos.y, p.radius, COLORS.wallDark);
    disc(ctx, p.pos.x, p.pos.y - ART.seam, p.radius - ART.outline, COLORS.stone);
    ctx.strokeStyle = COLORS.stoneLight;
    ctx.lineWidth = ART.outline;
    ctx.beginPath();
    ctx.arc(p.pos.x, p.pos.y, p.radius - ART.pillarRim, Math.PI, TAU);
    ctx.stroke();
    disc(ctx, p.pos.x, p.pos.y, p.radius * ART.pillarInset, COLORS.stoneLight);
    rookMark(ctx, p.pos.x, p.pos.y, p.radius * ART.emblemScale);
  }
}

/** Layered metal, inlays and distinct silhouettes are shared by pickups and held weapons. */
export function drawWeapon(ctx: CanvasRenderingContext2D, weapon: WeaponType): void {
  const art = WEAPON_ART[weapon], finish = WEAPON_FINISH;
  ctx.save();
  ctx.strokeStyle = finish.edge;
  ctx.lineWidth = finish.outline;
  ctx.lineJoin = 'round';
  const plate = (rect: readonly [number, number, number, number], color: string): void => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(...rect, finish.corner);
    ctx.fill();
    ctx.stroke();
  };
  for (const rect of art.grips) plate(rect, finish.grip);
  for (const rect of art.barrels) plate(rect, finish.metal);
  ctx.beginPath();
  art.hull.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
  ctx.closePath();
  const metal = ctx.createLinearGradient(0, -8, 0, 8);
  metal.addColorStop(0, finish.light);
  metal.addColorStop(0.25, finish.metal);
  metal.addColorStop(0.5, finish.dark);
  metal.addColorStop(1, finish.edge);
  ctx.fillStyle = metal;
  ctx.fill();
  ctx.stroke();
  for (const rect of art.accents) plate(rect, WEAPON_STYLE[weapon].color);
  for (const rect of art.rails) plate(rect, finish.dark);
  if (art.core) {
    const [x, y, radius] = art.core;
    disc(ctx, x, y, radius + finish.outline, finish.edge);
    disc(ctx, x, y, radius, WEAPON_STYLE[weapon].color);
    disc(ctx, x, y, radius / 2, finish.light);
  }
  ctx.restore();
}
